import { matchPath } from 'react-router-dom';

import DiscoverNavIconSvg from '../global/SvgComponents/DiscoverNavIcon_svg';
import EarthNavIconSvg from '../global/SvgComponents/EarthNavIcon_svg';
import RewardsNavIconSvg from '../global/SvgComponents/RewardsNavIcon_svg';
import UserCircleSvg from '../global/SvgComponents/UserCircle_svg';

import {
  ROUTE_SIGNUP,
  ROUTE_LOGIN,
  ROUTE_HOME,
  ROUTE_LOGOUT,
  ROUTE_REDEEM,
  ROUTE_DASHBOARD,
  ROUTE_ACCOUNT_SETTINGS,
  ROUTE_REGISTER,
  ROUTE_WALLET,
  ROUTE_TAKE_BACK_BAGS,
  ROUTE_TAKE_BACK_BAGS_DETAILS,
  ROUTE_TAKE_BACK_BAGS_BATCH,
  ROUTE_MY_ACCOUNT,
  ROUTE_WEB_NOTIFICATIONS_INBOX,
  ROUTE_WEB_NOTIFICATIONS_INBOX_ACCOUNT,
  TYPE_SEARCH_PARAM,
  EMAIL_SEARCH_PARAM,
  ROUTE_REWARD,
  PROMO_ID_PARAM,
  ROUTE_REWARDS,
  WF_RETURN_URL_SEARCH_PARAM,
  CATEGORY_SEARCH_PARAM,
  ROUTE_OFFERS,
  SORT_SEARCH_PARAM,
  FILTER_SEARCH_PARAMS,
  ROUTE_REWARDS_OLD,
  ROUTE_DEALS,
  REGISTER_SIGNED_AT_PARAM,
  REGISTER_SIGN_PARAM,
  REGISTER_CODE_PARAM,
  REGISTER_DATA_PARAM,
  ROUTES_TREE,
  ROUTE_GIFT_CARDS,
  TAB_SEARCH_PARAM,
  ROUTE_DASHBOARD_REWARDS,
  ROUTE_IMPACT,
  REGISTER_FROM_CHECKOUT_PARAM,
  SUBSCRIPTION_FROM_CHECKOUT_PARAM,
  EMBED_LOGIN_BACKGROUND_SEARCH_PARAM,
  ROUTE_REWARDS_V2,
  ROUTE_DEAL_STORE_REWARD_ID,
  ROUTE_REWARDS_V2_STORE_REWARD_ID,
  ROUTE_DEAL_STORE_ID,
  ROUTE_REWARDS_V2_STORE_ID,
  ROUTE_REWARDS_V2_LIST,
  ROUTE_GIFT_CARDS_V2,
  ROUTE_OFFERS_V2,
  ROUTE_SUBSCRIPTION,
  ROUTE_UNLIMITED,
  ROUTE_TRASHIE_UNLIMITED,
  ROUTE_MEMBERSHIP,
  MEMBERSHIP_CONFIRM_PARAM,
  STRIPE_SETUP_INTENT_CLIENT_SECRET_PARAM,
  STRIPE_CHECKOUT_SESSION_ID_PARAM,
  SHOPIFY_CHECKOUT_COMPLETE_PARAM,
  ROUTE_ARTICLES,
  DEEP_LINK_STORAGE_KEY,
} from '../config/routes';
import safeStorage from './localStorage';

const isLoginPath = (path) => path === ROUTE_LOGIN;

const isSignupPath = (path) => path === ROUTE_SIGNUP;

const isTrackedPath = (path) => !isLoginPath(path) && !isSignupPath(path);

const isRedeemPath = (path) => path === ROUTE_REDEEM || path === ROUTE_REGISTER;

const isArticlesPath = (path) => (
  path === ROUTE_ARTICLES || path.startsWith(`${ROUTE_ARTICLES}/`)
);

// Where login should return a signed-out reader who starts it from an article
// (/articles/:slug and its products page). Null elsewhere, including the
// /articles list itself: that's where every signed-out visitor lands, so
// returning there would override the /rewards default for nearly all logins.
const getArticleLoginRedirect = ({ pathname, search = '', hash = '' }) => (
  pathname.startsWith(`${ROUTE_ARTICLES}/`) ? `${pathname}${search}${hash}` : null
);

// Call when a signed-out user starts login from the nav. Hands the article path
// to /login the way PrivateRoute does: the caller adds it as the `redirect` param,
// and DEEP_LINK_STORAGE_KEY covers the email-link round-trip in another tab.
// Outside articles it clears that key, so one left by an abandoned login from an
// article can't hijack this login's /rewards default.
const prepareArticleLoginRedirect = (location) => {
  const articleLoginRedirect = getArticleLoginRedirect(location);
  if (articleLoginRedirect) {
    safeStorage.setItem(DEEP_LINK_STORAGE_KEY, articleLoginRedirect);
  } else {
    safeStorage.removeItem(DEEP_LINK_STORAGE_KEY);
  }
  return articleLoginRedirect;
};

// Paths handled by plain <Route><Redirect/></Route> entries in App.js (i.e. legacy
// URLs that redirect to a current surface). PrivateRoute has to skip them or its
// own deep-link redirect to /login will race the redirect Route and win for unauth
// users — see Phase 1 of stale-code-removal plan.
const isPublicRedirectPath = (path) => {
  const exact = [
    ROUTE_HOME, // / -> /rewards (signed in) or /articles (signed out)
    '/profile', // -> /account
    ROUTE_DASHBOARD, // /earnings -> /rewards
    ROUTE_WALLET, // /wallet -> /articles
    ROUTE_OFFERS_V2, // /shop-and-earn -> /articles
    ROUTE_OFFERS, // /earn-trashiecash -> /articles
    ROUTE_REWARDS_V2, // /deals -> /articles
    ROUTE_GIFT_CARDS_V2, // /gift-cards -> /rewards
    `${ROUTE_GIFT_CARDS_V2}/list`, // /gift-cards/list -> /rewards
    ROUTE_GIFT_CARDS, // /trashiecash-gift-cards -> /rewards
    ROUTE_DEALS, // /trashiecash-deals -> /articles
    ROUTE_REWARDS, // /trashiecash-rewards -> /rewards
  ];
  if (exact.includes(path)) return true;

  const prefixes = [
    `${ROUTE_REWARDS_V2}/`, // /deals/list, /deals/store/*
    `${ROUTE_GIFT_CARDS}/`, // /trashiecash-gift-cards/brand/*
    `${ROUTE_DEALS}/`, // /trashiecash-deals/store/*, /trashiecash-deals/deal/*
    `${ROUTE_REWARDS}/`, // /trashiecash-rewards/store/*, /trashiecash-rewards/reward/*
    `${ROUTE_REWARD}/`, // /reward/:rewardId -> /rewards
    `${ROUTE_REWARDS_OLD}/store/`, // /rewards/store/* (active /rewards exact stays)
    `${ROUTE_REWARDS_OLD}${ROUTE_REWARD}/`, // /rewards/reward/:rewardId
  ];
  return prefixes.some((p) => path.startsWith(p));
};

const isContainerPath = (path) => (
  [
    ROUTE_HOME,
    ROUTE_REDEEM,
    ROUTE_REGISTER,
    ROUTE_SUBSCRIPTION,
    ROUTE_UNLIMITED,
    ROUTE_TRASHIE_UNLIMITED,
  ].includes(path)
);

const isTrashieContainerPublicCenteredPath = (path) => (
  [
    ROUTE_LOGIN,
    ROUTE_SIGNUP,
  ].includes(path)
);

const isTrashieContainerPath = (path) => (
  [
    ROUTE_LOGOUT,
    ROUTE_DASHBOARD,
    ROUTE_DASHBOARD_REWARDS,
    ROUTE_IMPACT,
    ROUTE_WALLET,
    ROUTE_TAKE_BACK_BAGS,
    ROUTE_TAKE_BACK_BAGS_DETAILS,
    ROUTE_TAKE_BACK_BAGS_BATCH,
    ROUTE_MY_ACCOUNT,
    ROUTE_WEB_NOTIFICATIONS_INBOX,
    ROUTE_WEB_NOTIFICATIONS_INBOX_ACCOUNT,
    ROUTE_REWARDS,
    ROUTE_OFFERS,
    ROUTE_GIFT_CARDS,
  ].includes(path) ||
  path === ROUTE_ACCOUNT_SETTINGS ||
  path.startsWith(`${ROUTE_ACCOUNT_SETTINGS}/`) ||
  path === ROUTE_ARTICLES ||
  path.startsWith(`${ROUTE_ARTICLES}/`) ||
  path.match(ROUTE_REWARD) ||
  path.match(ROUTE_REWARDS) ||
  path.match(ROUTE_DEALS) ||
  path.match(ROUTE_GIFT_CARDS) ||
  path.match(ROUTE_REWARDS_OLD) ||
  path.match(ROUTE_REWARDS_V2) ||
  path.match(ROUTE_GIFT_CARDS_V2) ||
  path.match(ROUTE_OFFERS_V2) ||
  path.match(ROUTE_MEMBERSHIP)
);

const isTrashieMembershipPath = (path) => (
  path.match(ROUTE_MEMBERSHIP)
);

function getSearchParam(query, searchParam) {
  if (![
    TYPE_SEARCH_PARAM,
    EMAIL_SEARCH_PARAM,
    PROMO_ID_PARAM,
    WF_RETURN_URL_SEARCH_PARAM,
    CATEGORY_SEARCH_PARAM,
    SORT_SEARCH_PARAM,
    TAB_SEARCH_PARAM,
    FILTER_SEARCH_PARAMS.TYPE,
    FILTER_SEARCH_PARAMS.MIN_VALUE,
    FILTER_SEARCH_PARAMS.MAX_VALUE,
    FILTER_SEARCH_PARAMS.OTHER,
    REGISTER_CODE_PARAM,
    REGISTER_SIGN_PARAM,
    REGISTER_SIGNED_AT_PARAM,
    REGISTER_DATA_PARAM,
    REGISTER_FROM_CHECKOUT_PARAM,
    SUBSCRIPTION_FROM_CHECKOUT_PARAM,
    EMBED_LOGIN_BACKGROUND_SEARCH_PARAM,
    MEMBERSHIP_CONFIRM_PARAM,
    STRIPE_SETUP_INTENT_CLIENT_SECRET_PARAM,
    STRIPE_CHECKOUT_SESSION_ID_PARAM,
    SHOPIFY_CHECKOUT_COMPLETE_PARAM,
  ].includes(searchParam)) {
    return '';
  }

  return query.has(searchParam) ? decodeURIComponent(query.get(searchParam)) : '';
}

const isFooterPath = (path) => {
  const routes = [
    ROUTE_DASHBOARD_REWARDS,
    ROUTE_IMPACT,
    ROUTE_TAKE_BACK_BAGS,
    ROUTE_MY_ACCOUNT,
    ROUTE_MEMBERSHIP,
  ];

  if (path === ROUTE_ARTICLES || path.startsWith(`${ROUTE_ARTICLES}/`)) {
    return true;
  }

  return routes.some(route => path === route);
};

// No footer-excluded routes currently. Kept as an extension point — the prior
// /membership/addresses page (its only entry) is now an in-drawer panel.
const isFooterExcludedPath = () => false;

const matchCurrentPath = route => matchPath(window.location.pathname, {
  path: route,
  exact: true,
});

const getRouteData = ({
  route = '',
  routeData = ROUTES_TREE,
  matchRoute = '',
  returnParent = false,
}) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const [childrenRoute, childrenData] of Object.entries(routeData.children)) {
    if (
      (matchRoute && matchRoute === childrenRoute) ||
      (!matchRoute && matchCurrentPath(childrenRoute))
    ) {
      return returnParent ? ({
        route,
        data: routeData,
      }) : ({
        route: childrenRoute,
        data: childrenData,
      });
    }

    if (childrenData.children && Object.keys(childrenData.children).length) {
      const returnRoute = getRouteData({
        route: childrenRoute,
        routeData: childrenData,
        matchRoute,
        returnParent,
      });

      if (returnRoute.route) {
        return returnRoute;
      }
    }
  }

  return {};
};

const getCurrentRouteParentRoute = () => (
  getRouteData({ returnParent: true }).route ?? ''
);

const getCurrentRouteTitle = (includeIcon = false) => {
  const { title = '', icon } = getRouteData({}).data ?? {};

  if (includeIcon && icon) {
    return (
      <>
        <img src={icon} alt="route icon" />
        {title}
      </>
    );
  }

  return title;
};

const getRouteTitle = (matchRoute) => {
  const { title = '' } = getRouteData({ matchRoute }).data ?? {};

  return title;
};

const checkRouteWithCloseButton = () => {
  const { withCloseButton = false } = getRouteData({}).data ?? {};

  return Boolean(withCloseButton);
};

const getCurrentRouteHeaderBackground = () => {
  const { headerBackground = '' } = getRouteData({}).data ?? {};
  return headerBackground;
};

const getNavigationItems = () => [
  {
    label: 'Articles',
    icon: DiscoverNavIconSvg,
    route: ROUTE_ARTICLES,
  },
  {
    label: 'Impact',
    icon: EarthNavIconSvg,
    route: ROUTE_IMPACT,
  },
  {
    label: 'Rewards',
    icon: RewardsNavIconSvg,
    route: ROUTE_DASHBOARD_REWARDS,
  },
  {
    label: getRouteTitle(ROUTE_MY_ACCOUNT),
    icon: UserCircleSvg,
    route: ROUTE_MY_ACCOUNT,
  },
];

const REWARDS_V2_REDIRECT_ROUTES = [
  {
    paths: [
      ROUTE_DEAL_STORE_REWARD_ID,
    ],
    redirectPath: ROUTE_REWARDS_V2_STORE_REWARD_ID,
  },
  {
    paths: [
      ROUTE_DEAL_STORE_ID,
    ],
    redirectPath: ROUTE_REWARDS_V2_STORE_ID,
  },
  {
    paths: [
      ROUTE_DEALS,
    ],
    redirectPath: ROUTE_REWARDS_V2_LIST,
  },
  {
    paths: [
      ROUTE_DASHBOARD,
      ROUTE_HOME,
    ],
    redirectPath: ROUTE_REWARDS_V2,
  },
  {
    paths: [
      ROUTE_OFFERS,
    ],
    redirectPath: ROUTE_OFFERS_V2,
  },
];

const getRewardsV2RedirectPath = ({
  pathname,
  search,
  hash,
}) => {
  const rewardsV2Path = REWARDS_V2_REDIRECT_ROUTES.reduce((result, { paths, redirectPath }) => {
    if (result) {
      return result;
    }

    const matchedPathInfo = paths
      .map(path => matchCurrentPath(path))
      .find(match => match);

    if (!matchedPathInfo) {
      return '';
    }

    const { params } = matchedPathInfo;

    return Object.keys(params).reduce(
      (path, param) => path.replace(`:${param}`, params[param]),
      redirectPath,
    );
  }, '');

  return rewardsV2Path && rewardsV2Path !== pathname
    ? `${rewardsV2Path}${search && search}${hash}`
    : '';
};

export {
  isLoginPath,
  isSignupPath,
  isTrackedPath,
  isRedeemPath,
  isArticlesPath,
  getArticleLoginRedirect,
  prepareArticleLoginRedirect,
  isContainerPath,
  isTrashieContainerPublicCenteredPath,
  isTrashieContainerPath,
  isTrashieMembershipPath,
  getSearchParam,
  isFooterPath,
  matchCurrentPath,
  getRouteData,
  getCurrentRouteTitle,
  getCurrentRouteParentRoute,
  getNavigationItems,
  getRewardsV2RedirectPath,
  checkRouteWithCloseButton,
  getCurrentRouteHeaderBackground,
  isFooterExcludedPath,
  isPublicRedirectPath,
};
